import Phaser from "phaser";
import { playSound } from "../utils/sound-utils";
import { Item, ItemDefinition, TILE_SIZE } from "../models/common";

interface ThrowableProjectile {
  sprite: Phaser.GameObjects.Image;
  targetX: number;
  targetY: number;
  itemDef: ItemDefinition;
  startTime: number;
  tween: Phaser.Tweens.Tween;
}

interface ExplosionAnimation {
  graphics: Phaser.GameObjects.Graphics;
  sprite: Phaser.GameObjects.Sprite;
  startTime: number;
  maxRadius: number;
  duration: number;
  centerX: number;
  centerY: number;
}

/**
 * Draws a full throwable preview: arc, target circle, and distance markers.
 * Use this for both desktop and touch controls.
 */
export function drawFullThrowablePreview(
  graphics: Phaser.GameObjects.Graphics,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  options?: {
    color?: number;
    arcHeight?: number;
    steps?: number;
    showTargetCircle?: boolean;
    showMarkers?: boolean;
    targetRadius?: number;
    markerCount?: number;
  },
) {
  const color = options?.color ?? 0xffff00;
  const arcHeight = options?.arcHeight ?? 50;
  const steps = options?.steps ?? 24;
  const showTargetCircle = options?.showTargetCircle ?? true;
  const showMarkers = options?.showMarkers ?? true;
  const targetRadius = options?.targetRadius ?? 15;
  const markerCount = options?.markerCount ?? 3;

  // Draw arc
  graphics.lineStyle(3, color, 0.8);
  graphics.beginPath();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = startX + (endX - startX) * t;
    const baseY = startY + (endY - startY) * t;
    const arcY = arcHeight * Math.sin(t * Math.PI);
    const y = baseY - arcY;
    if (i === 0) {
      graphics.moveTo(x, y);
    } else {
      graphics.lineTo(x, y);
    }
  }
  graphics.strokePath();

  // Draw target circle
  if (showTargetCircle) {
    graphics.lineStyle(2, color, 1);
    graphics.strokeCircle(endX, endY, targetRadius);
  }

  // Draw distance markers along the arc
  if (showMarkers && markerCount > 0) {
    for (let i = 1; i <= markerCount; i++) {
      const t = i / (markerCount + 1);
      const markerX = startX + (endX - startX) * t;
      const baseY = startY + (endY - startY) * t;
      const arcY = arcHeight * Math.sin(t * Math.PI);
      const markerY = baseY - arcY;
      graphics.fillStyle(color, 0.6);
      graphics.fillCircle(markerX, markerY, 2);
    }
  }
}

export class ThrowableHandler {
  private scene: Phaser.Scene;
  private projectiles: ThrowableProjectile[] = [];
  private explosions: ExplosionAnimation[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // Parabolic arc for Y (creates natural throwing arc)
  // Called when server notifies all clients to animate the throw
  public animateThrowFromServer(data: {
    playerId: string;
    itemDef: ItemDefinition;
    startX: number;
    startY: number;
    targetX: number;
    targetY: number;
    flightTime: number;
  }) {
    // Play throw sound effect
    playSound(this.scene, "throw", {
      tileX: Math.floor(data.startX / TILE_SIZE),
      tileY: Math.floor(data.startY / TILE_SIZE),
    });

    // Create projectile sprite using SpriteLoader approach
    const typeColumns: Record<string, number> = {
      weapon: 10,
      armor: 10,
      medic: 2,
      ammo: 10,
      items: 16,
    };
    const columns = typeColumns[data.itemDef.spriteSheet] || 4;
    const frame = data.itemDef.spriteY * columns + data.itemDef.spriteX;

    const sprite = this.scene.add.sprite(
      data.startX,
      data.startY,
      data.itemDef.spriteSheet,
      frame,
    );
    sprite.setOrigin(0.5);
    sprite.setScale(0.8);
    sprite.setDepth(1000);

    // Make sure UI camera ignores this sprite (game world element)
    if ((this.scene as any).uiCamera) {
      (this.scene as any).uiCamera.ignore(sprite);
    }

    // Animate the throw
    const throwTween = this.scene.tweens.add({
      targets: { t: 0 },
      t: 1,
      duration: data.flightTime,
      ease: "Linear",
      onUpdate: (tween) => {
        const progress = tween.getValue();
        if (progress === null) return;
        const currentX = data.startX + (data.targetX - data.startX) * progress;
        const arcHeight = 50;
        const baseY = data.startY + (data.targetY - data.startY) * progress;
        const arcY = arcHeight * Math.sin(progress * Math.PI);
        const currentY = baseY - arcY;
        sprite.setPosition(currentX, currentY);
      },
      onComplete: () => {
        sprite.setPosition(data.targetX, data.targetY);
        // TODO: Set a timer to remove the sprite after some time
        this.scene.time.delayedCall(data.itemDef.stats.waitTime - 100, () => {
          sprite.destroy();
        });
      },
    });

    // Tint effect: alternate between red and normal
    this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 120,
      yoyo: true,
      repeat: -1,
      onUpdate: (tween) => {
        const value = tween.getValue() ?? 0;
        if (value < 0.5) {
          sprite.setTint(0xff0000); // red
        } else {
          sprite.clearTint(); // normal
        }
      },
    });

    const projectile: ThrowableProjectile = {
      sprite,
      targetX: data.targetX,
      targetY: data.targetY,
      itemDef: data.itemDef,
      startTime: Date.now(),
      tween: throwTween,
    };
    this.projectiles.push(projectile);
  }
  // Called when server notifies all clients to animate the explosion
  public animateExplosionFromServer(data: any) {
    // Find item by ID
    this.createExplosion(data.x, data.y, data.aoeRadius);
  }

  private createExplosion(x: number, y: number, aoeRadius: number) {
    const maxRadius = aoeRadius * 32; // Convert tiles to pixels (assuming 32px per tile)
    const desiredWidth = aoeRadius * 2 * 32; // width in pixels (diameter)

    // Play explosion sound effect
    playSound(this.scene, "explosion", {
      tileX: Math.floor(x / TILE_SIZE),
      tileY: Math.floor(y / TILE_SIZE),
    });

    // Create explosion sprite and play animation
    const explosionSprite = this.scene.add.sprite(x, y, "explosion");
    explosionSprite.setOrigin(0.5);
    // explosion.png is 64x64 per frame, so scale width to desiredWidth
    const scale = desiredWidth / 64;
    explosionSprite.setScale(scale);
    explosionSprite.setDepth(1001);

    // Make sure UI camera ignores this sprite (game world element)
    if ((this.scene as any).uiCamera) {
      (this.scene as any).uiCamera.ignore(explosionSprite);
    }

    // If animation not yet created, create it
    if (!this.scene.anims.exists("explosion_anim")) {
      this.scene.anims.create({
        key: "explosion_anim",
        frames: this.scene.anims.generateFrameNumbers("explosion", {
          start: 0,
          end: 8,
        }),
        frameRate: 16,
        repeat: 0,
      });
    }
    explosionSprite.play("explosion_anim");

    // Destroy sprite after animation completes
    explosionSprite.on("animationcomplete", () => {
      explosionSprite.destroy();
    });
  }

  destroy() {
    // Clean up all projectiles
    this.projectiles.forEach((projectile) => {
      if (projectile.tween) {
        projectile.tween.stop();
      }
      projectile.sprite.destroy();
    });
    this.projectiles = [];

    // Clean up all explosions
    this.explosions.forEach((explosion) => {
      if (explosion.graphics) explosion.graphics.destroy();
      if (explosion.sprite) explosion.sprite.destroy();
    });
    this.explosions = [];
  }
}
